import { Badge, Separator } from '@cannect/new-components/atoms'
import { CustomSelect, TOption } from '@cannect/new-components/atoms/Form/CustomSelect'
import { Product } from '@cannect/new-components/molecules'
import { ProductDetailsDialog } from '@cannect/new-components/organisms'
import { TDigitalJourneyFormData } from '@cannect/pages/DigitalJourney/types'

type TCartProductItem = {
  product: TDigitalJourneyFormData['cart']['products'][0]
  handleRemoveProduct: (productId: number) => void
  handleChangeProductQuantity: (productId: number, quantity: number) => void
  handleChangeProductFlavor: (oldProductId: number, newProductId: number) => void
  loadingProducts: Record<number, boolean>
  isLastItem: boolean
}

const quantityOptions: TOption[] = Array.from({ length: 20 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1} ${i === 0 ? 'item' : 'itens'}`
}))

export const CartProductItem = ({
  product,
  handleRemoveProduct,
  handleChangeProductQuantity,
  handleChangeProductFlavor,
  loadingProducts,
  isLastItem
}: TCartProductItem) => (
  <div className="flex flex-col">
    <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
      <div className="relative flex gap-4">
        {product?.type === 'PRODUTO' || product?.type === 'TESTE_GENETICO' && (
          <Product.RemoveButton onRemove={() => handleRemoveProduct(product.product_id)} />
        )}

        {/* Imagem do produto */}
        <ProductDetailsDialog productId={product.product_id}>
          <Product.HeaderImage
            className="h-20 w-20 flex-shrink-0 cursor-pointer sm:h-24 sm:w-24"
            src={product?.media?.filter((value) => value.format === 'IMG')?.[0]?.path}
            alt={product?.name}
            isNationalProduct={product?.supplier?.id === 18}
            product={product}
          />
        </ProductDetailsDialog>

        {/* Informações do produto - coluna principal */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {/* Nome e volume */}
          <div>
            <ProductDetailsDialog productId={product.product_id}>
              <h3 className="cursor-pointer pr-8 text-sm font-semibold leading-tight text-neutral-900 hover:text-primary-700 sm:text-base">
                {product?.name}
              </h3>
            </ProductDetailsDialog>
            {product?.medical_data?.volume && product?.type === 'PRODUTO' && (
              <p className="text-xs text-neutral-600 sm:text-sm">
                {product.medical_data.volume}
                {product?.medical_data?.unit_of_measurement?.toLowerCase()}
              </p>
            )}
            {product?.brand?.name && <p className="text-xs text-neutral-500 sm:text-sm">{product.brand.name}</p>}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {product?.class?.name && product?.type === 'PRODUTO' && (
              <Badge variant="neutral" className="text-xs">
                {product?.class?.name}
              </Badge>
            )}
            {product?.flavor && product?.type === 'PRODUTO' && (
              <Badge className="bg-flavors-default-100 text-xs text-flavors-default-900">
                {product?.flavor?.name || 'Natural'}
              </Badge>
            )}
          </div>

          {/* Controles - layout horizontal para aproveitar max-w-4xl */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            {product?.type === 'PRODUTO' || product?.type === 'TESTE_GENETICO' && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">Quantidade:</span>
                  <CustomSelect
                    isSearchable={false}
                    isClearable={false}
                    customClassNames={{
                      control: 'px-2 gap-2 min-w-[80px]',
                      dropdownIndicator: 'text-gray-400',
                      option: 'text-gray-400'
                    }}
                    variant="outlined"
                    options={quantityOptions}
                    value={quantityOptions.find((option) => option.value === product.product_quantity)}
                    onChange={(option) => {
                      if (option) {
                        handleChangeProductQuantity(product.product_id, option.value as number)
                      }
                    }}
                    isLoading={loadingProducts[product.product_id]}
                    size="small"
                  />
                </div>

                {product?.flavors && product?.flavors.length > 1 && (
                  <Product.FlavorSelect
                    className="text-xs text-primary-700 underline-offset-2 hover:underline"
                    flavorsList={product?.flavors || []}
                    onChange={(newProductId) => handleChangeProductFlavor(product.product_id, newProductId)}
                    value={product?.flavor?.name || 'Natural'}
                  />
                )}
              </div>
            )}
            {/* Preço */}
            <div className="flex justify-end">
              <Product.Price
                currentPrice={product?.real_price}
                promotionalPrice={product?.valid_promotion?.promotional_price}
                className="whitespace-nowrap text-base font-semibold"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    {!isLastItem && <Separator className="mt-4" />}
  </div>
)
